/* eslint-disable no-return-await */
import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import getIsRoktEnabledForCompletePage from '../../v2/selectors/RoktSelectors/getIsRoktEnabledForCompletePage';
import getPrivacyConsentFromPrivacyGroup from '../../v2/selectors/RoktSelectors/getPrivacyConsentFromPrivacyGroup';
import getRoktIdForCompletePage from '../../v2/selectors/RoktSelectors/getRoktIdForCompletePage';
import getIsRoktSandboxEnabledForCompletePage from '../../v2/selectors/RoktSelectors/getIsRoktSandboxEnabledForCompletePage';
import DomUtils from '../../v2/utils/DomUtils';
import Track from '../../v2/utils/tracking';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import {getStart} from '../../v2/selectors/selectors';

export function RoktCompletePage(props) {
  const loadedRokt = useRef(false);
  
  const enableRoktJS = async () => {
    await new Promise((resolve) => {
      if (window.Rokt) {
        resolve();
      } else {
        document.getElementById('rokt-launcher').onload = resolve;
      }
      
      return window.Rokt;
    });
  };
  
  const createLauncher = async () => await window.Rokt.createLauncher({
    accountId: props.roktId,
    sandbox: props.sandbox,
    noFunctional: !props.consentedToTargetingPrivacyConsentGroups,
    noTargeting: !props.consentedToTargetingPrivacyConsentGroups,
  });
  
  const attributes = {
    confirmationref: props.confirmationRef,
    country: props.country,
    emailenc: props.emailEnc,
    firstnameenc: props.firstNameEnc,
    lastnameenc: props.lastNameEnc,
    ccbinenc: props.ccBinEnc,
    cartItems: props.cartItems,
    paymenttype: props.paymentType,
  };
  
  const getSelection = async launcher => await launcher.selectPlacements({
    identifier: 'confirmation',
    attributes,
  });
  
  useEffect(() => {
    if (!loadedRokt.current) {
      DomUtils.addScript(
        'https://apps.rokt.com/wsdk/integrations/launcher.js',
        true,
        '',
        'high',
        true,
        'rokt-launcher',
      ).then(() => {
        loadedRokt.current = true;
        
        try {
          doMetric('Rokt', [{name: 'loaded', value: true}], [], 'impression');
        } catch (error) {
          Track.track(error);
        }
        
        enableRoktJS().then(() => {
          createLauncher().then((launcher) => {
            getSelection(launcher).then(((selection) => {
              selection.on('POSITIVE_ENGAGEMENT').subscribe(() => {
                selection.setAttributes({...attributes});
              });
            }));
          });
        }).catch(e => Track.error(`Error using ROKT script. ${e}`));
      }).catch(e => Track.error(`Error laoding ROKT script. ${e}`));
    }
  }, [props.roktCompleteEnabled, props.roktId, loadedRokt, props.confirmationRef, props.country, props.emailEnc,
    props.firstNameEnc, props.lastNameEnc, props.ccBinEnc, props.paymentType]);
  
  return <div id="rokt-placeholder" data-cs-mask />;
}

RoktCompletePage.propTypes = {
  roktCompleteEnabled: PropTypes.bool,
  roktId: PropTypes.string,
  sandbox: PropTypes.bool,
  confirmationRef: PropTypes.string,
  country: PropTypes.string,
  emailEnc: PropTypes.string,
  firstNameEnc: PropTypes.string,
  lastNameEnc: PropTypes.string,
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      productName: PropTypes.string,
      majorCat: PropTypes.string,
    }),
  ),
  ccBinEnc: PropTypes.number,
  paymentType: PropTypes.string,
  
};

const mapStateToProps = (state) => {
  const start = getStart(state);
  const confirmationRef = start?.orderNumber;
  const country = start?.customer?.location;
  const emailEnc = start?.encryptedValues?.emailEnc;
  const firstNameEnc = start?.encryptedValues?.firstNameEnc;
  const lastNameEnc = start?.encryptedValues?.lastNameEnc;
  const ccBinEnc = start?.encryptedValues?.ccBinEnc;
  const cartItems = start?.order?.basketItems.map(item => ({
    sku: item?.productId,
    productName: item?.productTitle,
    majorCat: item?.productTypeName,
  }));
  const paymentType = start?.selectedPaymentMethod;
  
  return {
    roktCompleteEnabled: getIsRoktEnabledForCompletePage(state),
    roktId: getRoktIdForCompletePage(state),
    sandbox: getIsRoktSandboxEnabledForCompletePage(state),
    confirmationRef,
    country,
    emailEnc,
    firstNameEnc,
    lastNameEnc,
    ccBinEnc,
    cartItems,
    paymentType,
    consentedToTargetingPrivacyConsentGroups: !!getPrivacyConsentFromPrivacyGroup(state)
  };
};

export default connect(mapStateToProps)(RoktCompletePage);
